import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '../lib/api';
import { resolveLocale } from '../lib/dateFormat';
import { useUIStore } from '../store/index';
import { Project, ProjectDocument, EvictionCandidate } from '../types';
import Modal from '../components/Modal';
import { useConfirm } from '../components/ConfirmDialogProvider';

interface QuotaConflict {
  file: File;
  projectId: string;
  candidates: EvictionCandidate[];
  neededBytes: number;
}

interface DocumentRow extends ProjectDocument {
  project_name: string;
  project_key: string;
}

const Documents: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useUIStore();
  const confirm = useConfirm();

  const [projects, setProjects] = useState<Project[]>([]);
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterProjectId, setFilterProjectId] = useState('');

  const [showUpload, setShowUpload] = useState(false);
  const [uploadProjectId, setUploadProjectId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [quotaConflict, setQuotaConflict] = useState<QuotaConflict | null>(null);
  const [confirmingEviction, setConfirmingEviction] = useState(false);

  const [deleteLoadingId, setDeleteLoadingId] = useState<string | null>(null);
  const [downloadLoadingId, setDownloadLoadingId] = useState<string | null>(null);

  // There is no workspace-wide "list all documents" endpoint — documents
  // are only listable per project (GET /projects/:projectId/documents).
  // Fetch every project's documents in parallel and merge them into one
  // flat, sortable table with a project filter column.
  const fetchDocuments = useCallback(async (projectList: Project[]) => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.all(
        projectList.map((p) =>
          api.get(`/projects/${p.id}/documents`).then((res) => ({
            project: p,
            docs: ((res.data as any)?.data || []) as ProjectDocument[]
          }))
        )
      );
      const merged: DocumentRow[] = results.flatMap(({ project, docs }) =>
        docs.map((d) => ({ ...d, project_name: project.name, project_key: project.project_key }))
      );
      merged.sort((a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime());
      setDocuments(merged);
    } catch (err: any) {
      setError(getApiErrorMessage(err, t('documents.errors.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/projects');
        const projectList = ((res.data as any)?.data || []) as Project[];
        if (cancelled) return;
        setProjects(projectList);
        setUploadProjectId(projectList.find((p) => p.is_default === 1)?.id || projectList[0]?.id || '');
        await fetchDocuments(projectList);
      } catch (err: any) {
        if (!cancelled) {
          setError(getApiErrorMessage(err, t('documents.errors.loadFailed')));
          setLoading(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredDocuments = filterProjectId
    ? documents.filter((d) => d.project_id === filterProjectId)
    : documents;

  const resetUploadForm = () => {
    setUploadError(null);
    setQuotaConflict(null);
    setUploadProjectId(projects.find((p) => p.is_default === 1)?.id || projects[0]?.id || '');
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadProjectId) return;

    setUploading(true);
    setUploadError(null);
    setQuotaConflict(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/projects/${uploadProjectId}/documents`, formData);
      await fetchDocuments(projects);
      setShowUpload(false);
      resetUploadForm();
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response?.status === 409 && err.response.data?.error?.code === 'QUOTA_EXCEEDED') {
        setQuotaConflict({
          file,
          projectId: uploadProjectId,
          candidates: err.response.data.data.candidates,
          neededBytes: err.response.data.data.neededBytes
        });
      } else {
        setUploadError(getApiErrorMessage(err, t('documents.errors.uploadFailed')));
      }
    } finally {
      setUploading(false);
      // Allow re-selecting the same file (browsers don't fire onChange
      // again for an identical path otherwise) — irrelevant for the
      // success path (modal closes) but matters if the user cancels the
      // eviction confirmation and wants to retry.
      e.target.value = '';
    }
  };

  const handleConfirmEviction = async () => {
    if (!quotaConflict) return;
    setConfirmingEviction(true);
    setUploadError(null);

    const formData = new FormData();
    formData.append('file', quotaConflict.file);
    formData.append('confirmEviction', 'true');

    try {
      await api.post(`/projects/${quotaConflict.projectId}/documents`, formData);
      await fetchDocuments(projects);
      setShowUpload(false);
      resetUploadForm();
    } catch (err: any) {
      setUploadError(getApiErrorMessage(err, t('documents.errors.uploadFailed')));
      setQuotaConflict(null);
    } finally {
      setConfirmingEviction(false);
    }
  };

  const handleDelete = async (doc: DocumentRow) => {
    if (!(await confirm(t('documents.confirmations.deleteFile'), { variant: 'danger' }))) return;
    setDeleteLoadingId(doc.id);
    setError(null);
    try {
      await api.delete(`/projects/${doc.project_id}/documents/${doc.id}`);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err: any) {
      setError(getApiErrorMessage(err, t('documents.errors.deleteFailed')));
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const handleDownload = async (doc: DocumentRow) => {
    setDownloadLoadingId(doc.id);
    setError(null);
    try {
      const res = await api.get(`/projects/${doc.project_id}/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(getApiErrorMessage(err, t('documents.errors.downloadFailed')));
    } finally {
      setDownloadLoadingId(null);
    }
  };

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString(resolveLocale(language), {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('documents.title')}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('documents.subtitle')}</p>
        </div>
        <button
          onClick={() => { resetUploadForm(); setShowUpload(true); }}
          disabled={projects.length === 0}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto justify-center"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('documents.uploadButton')}
        </button>
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <Modal>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('documents.uploadModalTitle')}</h2>
              <button
                onClick={() => setShowUpload(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-5">
              {uploadError && (
                <div className="bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-900/40 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                  {uploadError}
                </div>
              )}

              {quotaConflict ? (
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-900/40 dark:text-yellow-400 px-4 py-3 rounded-lg text-sm space-y-1">
                    <p className="font-medium">{t('fileQuota.title')}</p>
                    <p>{t('fileQuota.message')}</p>
                    <p className="text-xs opacity-80">{t('fileQuota.neededSpace', { size: formatSize(quotaConflict.neededBytes) })}</p>
                  </div>

                  <ul className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    {quotaConflict.candidates.map((c) => (
                      <li key={c.id} className="px-4 py-2 flex justify-between items-center text-sm bg-white dark:bg-gray-800">
                        <div className="min-w-0">
                          <p className="text-gray-900 dark:text-gray-100 truncate">{c.original_name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{t(`fileQuota.types.${c.type}`)} · {formatDate(c.uploaded_at)}</p>
                        </div>
                        <span className="text-gray-500 dark:text-gray-400 shrink-0 ml-3">{formatSize(c.size_bytes)}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setQuotaConflict(null)}
                      disabled={confirmingEviction}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {t('fileQuota.cancel')}
                    </button>
                    <button
                      onClick={handleConfirmEviction}
                      disabled={confirmingEviction}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {confirmingEviction && (
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      )}
                      {confirmingEviction ? t('fileQuota.processing') : t('fileQuota.confirmAndUpload')}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('documents.selectProject')} *</label>
                    <select
                      value={uploadProjectId}
                      onChange={(e) => setUploadProjectId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name} ({p.project_key})</option>
                      ))}
                    </select>
                  </div>

                  <div className="p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                    <label className="flex flex-col items-center cursor-pointer">
                      <svg className="w-8 h-8 text-gray-400 dark:text-gray-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('documents.chooseFile')}</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">{t('documents.maxSize')}</span>
                      <input
                        type="file"
                        className="hidden"
                        onChange={handleUpload}
                        disabled={uploading || !uploadProjectId}
                      />
                    </label>
                    {uploading && (
                      <div className="flex items-center justify-center mt-2 text-sm text-blue-600 dark:text-blue-400">
                        <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t('documents.uploading')}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="ml-3 text-gray-600 dark:text-gray-400">{t('documents.loading')}</span>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-900/40 dark:text-yellow-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Document table */}
      {!loading && !error && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('documents.documentList')}</h2>
            <p className="text-xs text-gray-600 dark:text-gray-400">{t('documents.documentCount')}</p>
          </div>

          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <div className="space-y-1">
                      <div>{t('documents.columns.project')}</div>
                      <select
                        value={filterProjectId}
                        onChange={(e) => setFilterProjectId(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full normal-case font-normal text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">{t('documents.allProjects')}</option>
                        {projects.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('documents.columns.fileName')}</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('documents.columns.size')}</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('documents.columns.uploadedAt')}</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('documents.columns.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredDocuments.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {doc.project_name} <span className="text-xs">({doc.project_key})</span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 max-w-xs truncate">
                      {doc.original_name}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatSize(doc.size_bytes)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(doc.uploaded_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDownload(doc)}
                          disabled={downloadLoadingId === doc.id}
                          className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors disabled:opacity-50"
                          title={t('documents.download') as string}
                        >
                          {downloadLoadingId === doc.id ? (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(doc)}
                          disabled={deleteLoadingId === doc.id}
                          className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                          title={t('documents.delete') as string}
                        >
                          {deleteLoadingId === doc.id ? (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="lg:hidden px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <select
              value={filterProjectId}
              onChange={(e) => setFilterProjectId(e.target.value)}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2"
            >
              <option value="">{t('documents.allProjects')}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div className="lg:hidden divide-y divide-gray-100 dark:divide-gray-700">
            {filteredDocuments.map((doc) => (
              <div key={doc.id} className="px-4 py-3">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">{doc.original_name}</p>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{doc.project_name} ({doc.project_key}) · {formatSize(doc.size_bytes)} · {formatDate(doc.uploaded_at)}</p>
                <div className="mt-2 flex items-center gap-2">
                  <button onClick={() => handleDownload(doc)} disabled={downloadLoadingId === doc.id} className="px-3 py-1.5 text-xs font-medium border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50">{t('documents.download')}</button>
                  <button onClick={() => handleDelete(doc)} disabled={deleteLoadingId === doc.id} className="px-3 py-1.5 text-xs font-medium border border-red-200 dark:border-red-900/40 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50">{t('documents.delete')}</button>
                </div>
              </div>
            ))}
          </div>

          {filteredDocuments.length === 0 && (
            <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
              {t('documents.noDocuments')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Documents;
